import {
  calculateAverageDifficulty,
  deserify,
  getDifficultyLevelString,
  IconWrapper,
  IStyles,
  pxToRem,
  pxTovW,
  QuantityButtonV2,
  theme,
} from '@geneo2-web/shared-ui';
import {
  Box,
  Button,
  Divider,
  SxProps,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  getModeOfConductionIcon,
  getModeOfConductionString,
  totalNoQuestions,
} from '../../../../../utils/functions';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../reduxStore/reduxHooks';
import { DifficultyLevelEnum } from '@protos/content_management/content.db_pb';
import {
  MarkingSchemeEnum,
  ModeOfConductionEnum,
} from '@protos/learning_management/lms.db_pb';
import CheckIcon from '@mui/icons-material/Check';
import { BloomLevelComponent } from '../../../components/BloomLevelComponent';
import {
  getQuestionTypesArrayFromQuestionsList,
  markingSchemeOptions,
} from '../functions';
import {
  setAssessmentCreationType,
  setFAEditStep,
} from '../../../reducer/assessmentFlow.slice';
import { SetStateAction } from 'react';
import { MarkingSchemeTooltip } from 'libs/shared-ui/src/components/composites/MarkingSchemeTooltip';
import { InfoBarComponent } from '../../../components/InfoBarComp';
import { MAXIMUM_MARKS_LIMIT } from '../../../utils/assessment.utils';

interface IBloomConductionQuestionDetails {
  timeOfQuestions: () => string;
  getBloomLevelData: () => {
    title: string;
    Questions: number;
    color: string;
  }[];
  setEditPopupOpen: (value: SetStateAction<boolean>) => void;
  setEditClick: (value: SetStateAction<boolean>) => void;
  isFormativeAssessment: () => boolean;
  rootStyles?: SxProps;
  modeOfConduction?: ModeOfConductionEnum;
}

export const BloomConductionQuestionDetails = (
  props: IBloomConductionQuestionDetails
) => {
  const {
    timeOfQuestions,
    getBloomLevelData,
    setEditPopupOpen,
    setEditClick,
    isFormativeAssessment,
    rootStyles,
    modeOfConduction,
  } = props;

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const { class_subject_info } = deserify(
    useAppSelector((state) => state.homeDashboard)
  );
  const {
    questions_count,
    module_filtered_questions,
    assesment_creation_type,
    fa_selected_chapter_data,
  } = deserify(useAppSelector((state) => state.assessment));

  // --------------------------------------------------------------

  const styles: IStyles = {
    bloomContainer: {
      display: { xs: 'flex', md: 'flex' },
      flexDirection: 'column',
      gap: { xs: pxToRem(11), md: pxTovW(20) },
      background: '#FFFFFF',
      borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
      border: { xs: 'none', md: '1px solid #E0E2E4' },
      padding: { xs: pxToRem(0), md: pxTovW(12) },
      boxShadow: { xs: 'none', md: `0px 0px ${pxToRem(5)} #E7E7E7D9` },
      ...rootStyles,
    },
    infoBox: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      // mb: { xs: pxToRem(20), md: pxTovW(40) },
      // paddingY: { xs: pxToRem(10), md: pxTovW(20) },
    },
  };

  // --------------------------------------------------------------

  const getDifficultyLevelStats = () => {
    const questions = module_filtered_questions?.questions || [];
    let easy = 0;
    let medium = 0;
    let hard = 0;

    questions.forEach((q) => {
      switch (q?.questionMeta?.difficultyLevel) {
        case DifficultyLevelEnum.DIFFICULTY_LEVEL_LOW:
          easy++;
          break;
        case DifficultyLevelEnum.DIFFICULTY_LEVEL_MEDIUM:
          medium++;
          break;
        case DifficultyLevelEnum.DIFFICULTY_LEVEL_HIGH:
          hard++;
          break;
      }
    });

    return [
      {
        iconName: 'stat-green',
        heading: easy,
        headingSubText: 'Qs',
        subHeading: 'Easy',
      },
      {
        iconName: 'stat-yellow',
        heading: medium,
        headingSubText: 'Qs',
        subHeading: 'Medium',
      },
      {
        iconName: 'stat-red',
        heading: hard,
        headingSubText: 'Qs',
        subHeading: 'Hard',
      },
    ];
  };

  // --------------------------------------------------------------

  return (
    <Box sx={styles.bloomContainer}>
      {assesment_creation_type.markingScheme !==
      MarkingSchemeEnum.MARKING_SCHEME_JEE ? (
        <BloomLevelComponent
          bloomLevelData={getBloomLevelData()}
          editClickHandler={
            fa_selected_chapter_data
              ? () => {
                  dispatch(setFAEditStep('settings'));
                  setEditPopupOpen(true);
                  setEditClick(true);
                }
              : undefined
          }
        />
      ) : fa_selected_chapter_data ? (
        <Button
          sx={{
            display: { xs: 'none', md: 'flex' },
            p: '0',
            marginRight: 'auto',
          }}
          onClick={() => {
            dispatch(setFAEditStep('settings'));
            setEditPopupOpen(true);
            setEditClick(true);
          }}
        >
          <IconWrapper
            name="edit"
            type="png"
            parentFolder="icons"
            size="large"
          />
        </Button>
      ) : null}

      <Divider orientation="horizontal" flexItem />

      <Box sx={styles.infoBox}>
        <IconDataLabel
          iconName="questions"
          data={totalNoQuestions(questions_count).toString()}
          label="Questions"
        />
        <IconDataLabel
          iconName="clock"
          data={timeOfQuestions()}
          label="Minutes"
        />
        <IconDataLabel
          iconName="bar-graph"
          data={getDifficultyLevelString(
            calculateAverageDifficulty(
              module_filtered_questions?.questions
                .map((a) => a?.questionMeta?.difficultyLevel)
                .filter(
                  (level): level is DifficultyLevelEnum => level !== undefined
                ) || []
            )
          )}
          label="Difficulty Level"
          hideDivider
        />
      </Box>

      <Divider orientation="horizontal" flexItem />

      {/* //! Conduction Via */}
      {/* {class_subject_info?.isClickerSupported ? ( */}
      <Box
        sx={{
          display: 'flex',
          gap: { xs: pxToRem(10), md: pxTovW(20) },
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: pxToRem(10), md: pxTovW(20) },
          }}
        >
          <Typography variant="h3" fontWeight="bold">
            Conduction Via
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: pxToRem(10),
              alignItems: 'center',
            }}
          >
            <IconWrapper
              name={getModeOfConductionIcon(modeOfConduction)}
              type="png"
              parentFolder="icons"
              customSx={{
                width:
                  modeOfConduction ===
                  ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER
                    ? { xs: pxToRem(20), lg: pxTovW(36) }
                    : { xs: pxToRem(35), lg: pxTovW(40) },
                height: { xs: pxToRem(40), lg: pxTovW(42) },
              }}
            />
            <Typography variant="h4" fontWeight="bold">
              {getModeOfConductionString(modeOfConduction)}
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* ) : null} */}

      {/* //! Marking Scheme */}
      {isFormativeAssessment() && assesment_creation_type.markingScheme ? (
        <>
          <Divider orientation="horizontal" flexItem />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              // marginY: { xs: pxToRem(10) },
              gap: { xs: pxToRem(10) },
            }}
          >
            <Typography variant="h3" fontWeight={600} fontFamily={'poppins'}>
              Marking Scheme
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: pxToRem(12) },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: { xs: pxToRem(5) },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '50%',
                    border: '1px solid #007CDC',
                    width: { xs: pxToRem(24), md: pxTovW(24) },
                    height: { xs: pxToRem(24), md: pxTovW(24) },
                    backgroundColor: '#E0F1FF',
                    justifyContent: 'center',
                    padding: pxTovW(2),
                  }}
                >
                  <CheckIcon
                    sx={{ width: { xs: pxToRem(20), md: pxTovW(20) } }}
                    color="primary"
                  />
                </Box>
                <Typography variant="h5">
                  {markingSchemeOptions.find(
                    (scheme: any) =>
                      scheme.type === assesment_creation_type.markingScheme
                  )?.title || ''}
                </Typography>
                {assesment_creation_type.markingScheme ===
                  MarkingSchemeEnum.MARKING_SCHEME_JEE && (
                  <MarkingSchemeTooltip />
                )}
                {assesment_creation_type.markingScheme ===
                  MarkingSchemeEnum.MARKING_SCHEME_CUSTOM && (
                  <Box sx={{ marginLeft: pxToRem(10) }}>
                    {' '}
                    <QuantityButtonV2
                      value={assesment_creation_type.marksPerQuestion || 1}
                      max={MAXIMUM_MARKS_LIMIT}
                      onChange={(nextValue: number) => {
                        dispatch(
                          setAssessmentCreationType({
                            ...assesment_creation_type,
                            marksPerQuestion: nextValue,
                          })
                        );
                      }}
                      onIncrement={() => {
                        dispatch(
                          setAssessmentCreationType({
                            ...assesment_creation_type,
                            marksPerQuestion: Math.min(
                              (assesment_creation_type.marksPerQuestion || 1) +
                                1,
                              MAXIMUM_MARKS_LIMIT
                            ),
                          })
                        );
                      }}
                      onDecrement={() => {
                        dispatch(
                          setAssessmentCreationType({
                            ...assesment_creation_type,
                            marksPerQuestion: Math.max(
                              (assesment_creation_type.marksPerQuestion || 1) -
                                1,
                              1
                            ),
                          })
                        );
                      }}
                    />{' '}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </>
      ) : null}

      {/* //! Breakdown of Difficulty Level */}
      <Divider sx={{ bgcolor: '#E0E0E0', height: '1px', opacity: 0.5 }} />
      <Box>
        <Typography
          variant="h3"
          fontWeight={600}
          fontFamily={'poppins'}
          marginBottom={pxToRem(10)}
        >
          Breakdown of Difficulty Level
        </Typography>
        <InfoBarComponent contentList={getDifficultyLevelStats()} />
      </Box>

      {/* //! Question Types */}
      {isFormativeAssessment() ? (
        <>
          <Divider orientation="horizontal" flexItem />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: pxToRem(10) },
            }}
          >
            <Typography variant="h3" fontWeight={600} fontFamily={'poppins'}>
              Question Type
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: pxToRem(12) },
              }}
            >
              {getQuestionTypesArrayFromQuestionsList(
                module_filtered_questions
              ).map((question_type, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: { xs: pxToRem(5) },
                  }}
                >
                  <IconWrapper
                    name="question-blue"
                    type="png"
                    parentFolder="icons"
                    size="md"
                  />
                  <Typography variant="h5">{question_type.title}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      ) : null}
    </Box>
  );
};

interface IDetails {
  iconName: string;
  data: string;
  label: string;
  hideDivider?: boolean;
}
export const IconDataLabel = (props: IDetails) => {
  const { iconName, data, label, hideDivider } = props;

  const styles: IStyles = {
    iconDetailsComp: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: { xs: pxToRem(5), md: pxTovW(10) },
    },
    iconAndData: {
      display: 'flex',
      alignItems: 'center',
      gap: { xs: pxToRem(10), md: pxTovW(15) },
    },
  };

  return (
    <>
      <Box sx={styles.iconDetailsComp}>
        <Box sx={styles.iconAndData}>
          <IconWrapper
            name={iconName}
            size="md"
            parentFolder="icons"
            type="png"
          />

          <Typography
            variant="h2"
            fontWeight="bold"
            sx={{ fontSize: { xs: pxToRem(16), md: pxTovW(24) } }}
          >
            {data}
          </Typography>
        </Box>

        <Typography variant="h4">{label}</Typography>
      </Box>

      {!hideDivider ? (
        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderRight: '1px dashed rgba(0, 0, 0, 0.12)' }}
        />
      ) : null}
    </>
  );
};
